// src/com/auto_plan_upload/AutoPlanUploadUploader.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import {
    DialogContent, Button, DialogActions,
    CircularProgress, Tooltip
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload'; // upload file
import DoneIcon from '@mui/icons-material/Done'; // close the dialog.
import FindInPageIcon from '@mui/icons-material/FindInPage'; // search file
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant'; // error
import { useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { AppWord, AppStyle } from '../../value/AppConst';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import DraggableEditor from '../../common/DraggableEditor';
import { userInfoRepo, authTableTreeRepo } from '../../value/AppModel';
import { usePapaParse } from 'react-papaparse';
import { UPLOAD_AUTO_PLAN_UPLOAD } from './AutoPlanUploadGql'; // 여기서 올리고, 여기서 반응을 보여줘야 함.
import IsValid from '../../value/IsValid';
import FlexyTable from '../../common/flexytable/FlexyTable';
import InputUtil from '../../value/InputUtil';
import { AutoPlanUploadDictionary } from './AutoPlanUploadDict';
import { ContentHeader, ControlBox, CountLabel, ToolBox } from '../../common/TopViewControl';
import SearchBox from '../../common/SearchBox';
import VzmCheckbox from '../../common/VzmCheckbox';
import { useConfirmDialog } from '../../common/AppConfirmDialog';

const tableProps = AutoPlanUploadDictionary.uploadTableProps;
// extract for upload from InputMap
const uploadMap = AutoPlanUploadDictionary.inputMap;
const EmptyCsvData = {data:[], errors:[], meta:{}};

function checkItem(item) {
    const errors = [];
    for(const id of Object.keys(uploadMap)) {
        const label = uploadMap[id].label;
        if(IsValid.nullString(item[id])) {
            if(uploadMap[id].required && uploadMap[id].toAdd) {
                errors.push(`${label} 없음.`);
            }
        }
        else {
            const check = uploadMap[id].check;
            if(check) {
                const good = check(item[id]);
                if(!good) {
                    errors.push(`${label} 오류.`);
                }
            }
        }
    }
    if(errors.length > 0) {
        item.ok = false;
        item.message = errors.join("\n");
    }
    else {
        item.ok = true;
        item.message = 'Ready';
    }
    return item;
}

// replace someKey with proper one
function checkAndMap(someKey, data) {
    let countError = 0;
    const records = data.map((array) => {
        let item = {
            planDate: array[0],
            storeId: array[1], // key
            leftOver: array[2],
            totVol: array[3],
            nBulkPt: array[4],
            yBulkPt: array[5],
            oBulkPt: array[6],
            pbsPt15: array[7],
            storeCpp: array[8],
            prePt17: array[9],
            dayPt04: array[10],
            dayPt11: array[11],
            dayPt15: array[12],
            dayPt18: array[13],
            totalPt: array[14],
            nextday04Pt: array[15],
            // check columns and adjust numbers
        }
        item = checkItem(item);
        if(!item.ok) countError++;
        return item;
    });
    return {records, countError};
}

/**
 * (AUTO_PLAN_UPLOAD) 정보 일괄 업로드.
 * 자동배차 준비 정보
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2025-03-17 09:58:10 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function AutoPlanUploadUploader({
    open,
    onClose,
}) {
    const [text, setText] = useState('');
    const sessionInfo = useReactiveVar(userInfoRepo);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const [csvData, setCsvData] = useState(EmptyCsvData); // {data:[], errors:[], meta:{}}
    const [tableData, setTableData] = useState([]);
    const [errorCount, setErrorCount] = useState(0);
    const [changed, setChanged] = useState(false);
    const [countToDisplay, setCountToDisplay] = useState(0);
    // const [msgDone, setMsgDone] = useState('');
    const [searchText, setSearchText] = useState('');
    const [useEucKr, setUseEucKr] = useState(false);
    const [filterError, setFilterError] = useState(false);
    const addAlert = useAlertSetter();
    const { readString } = usePapaParse();
    const openConfirmDialog = useConfirmDialog();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const checkAuth = useAuthorizationCheck();
    const fileInputRef = useRef(null);
    const uploadedRef = useRef(false);

    const [uploadAutoPlanUpload, responseUpload] = useMutation(UPLOAD_AUTO_PLAN_UPLOAD, {
        onCompleted: (data, option) => {onCompletedUpload(data, option);},
        onError: (error) => {
            addAlert({title: "상온배차 업로드 오류", message: [error.message]});
        }
    });

    useEffect(()=>{
        if(!open) {
            setText('');
            setCsvData(EmptyCsvData);
        }
    }, [open]);

    // 내부에서 다시 파일 선택할 수 있으므로 csvText에서 직접 처리하지 않고 csvText => text => csvData로 처리.
    useEffect(()=>{
        if(!IsValid.nullString(text)) {
            readString(text, {
                worker: true,
                complete: (result) => {
                    const { data, errors, meta } = result;
                    // data.shift(); // remove header.
                    const records
                    = data.filter((row) => /^\d{4}-\d{2}-\d{2}/.test(row[0]))
                    .map((row) => {
                        const newRow = row.slice(0, Object.keys(uploadMap).length); // 사용할 칼럼 수만큼 자른다. 확인!!
                        // 추가작업 필요하면 여기에. (데이터 변경, 계산 등)
                        return newRow;
                    });
                    setCsvData({data: records, errors, meta});
                }
            })
        }
    }, [text, readString]);

    useEffect(()=>{
        if(open) {
            const {records, countError} = checkAndMap(null, csvData.data);
            setTableData(records);
            // setMsgDone('');
            setErrorCount(countError);
            setChanged(true);
        }
    }, [csvData, sessionInfo, open]);

    const onCompletedUpload = (data, option) => {
        if(data.autoPlanUploadUpload.ok) {
            // reflect the result to the table.(ok, message, list {ok, message, arriveId})
            let countError = 0;
            const newTableData = [];
            for(const prev of tableData) {
                // ########################## Check this comparison ##########################
                const found = data.autoPlanUploadUpload.list.find((item)=>prev.storeId === item.storeId);
                if(found) {
                    prev.ok = found.ok;
                    prev.message = found.message;
                    newTableData.push(prev);
                    if(!found.ok) countError++;
                }
                else newTableData.push(prev);
            }
            setErrorCount(countError);
            setTableData(newTableData);
            setChanged(false); // 다시 올리지 않도록.
            enqueueSnackbar("상온배차 정보 업로드 완료", {variant: 'success'});
            // setMsgDone('[업로드 완료] ');
            uploadedRef.current = true;
        }
        else addAlert({title: "상온배차 정보 업로드", message: [data.autoPlanUploadUpload.message]});
    };

    const onClickUpload = () => {
        // 아래 filter에서 "row &&" 빼면 오류 발생함. row가 널이거나 undefined인 경우가 없는데도 발생함. 헉!
        const toUpload = tableData.filter((row)=>{
            if(row && row.ok) return true;
            else return false;
        }).map((row)=>InputUtil.getDataToSubmit(row, uploadMap, false));
        const param = {variables: {autoPlanUpload: {list:toUpload, dcCd: sessionInfo.user.dscIdAs}}};

        uploadAutoPlanUpload(param);
    };

    const onClickFindFile = () => {
        setText('');
        if(fileInputRef.current) {
            fileInputRef.current.value='';
            fileInputRef.current.click();
        }
    };

    const onCsvFileRead = (event) => {
        const content = event.target.result;
        setText(content);
    };

    const onChooseFile = (event) => {
        if(event.target.files.length > 0) {
            const file = event.target.files[0];
    
            if (file) {
                const reader = new FileReader();
                reader.onload = onCsvFileRead;
                reader.readAsText(file);
            }
        }
    };

    const alertErrorData = (row) => {
        const lines = row.message.split("\n").filter((line)=>line.length > 0);
        addAlert({title: `상온배차 정보 오류(${row.dcCd}-${row.storeId})`, message: lines});
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case 'error': alertErrorData(row); break;
            default: break;
        }
    };

    const onChangeFilterError = (id, value) => {
        setFilterError(value);
    };

    const askAndClose = async () => {
        if(changed) {
            const confirm =  await openConfirmDialog({
                title: "운송요청업로드",
                content: [
                    '변경된 내용이 있습니다.',
                    '창을 닫으시겠습니까?'
                ]
            });

            if(confirm) {
                onClose(uploadedRef.current);
            }
        }
        else onClose(uploadedRef.current);
    };

    const onClickClose = () => {
        askAndClose();
    };

    const canAdd = checkAuth(authTableTree.tree.pr_auto_plan_upload_add);

    const tools = [
        {id:'error', icon: <NotificationImportantIcon/>, color: AppStyle.mui.error, where: (row) => !row.ok}
    ];

    const loadingAny = responseUpload.loading;

    return(
        <DraggableEditor open={open} maxWidth='md'
            startIcon={<CloudUploadIcon size={AppWord.LARGE} />}
            title="상온배차 정보 업로드"
            fullScreen={true}
        >
            <DialogContent style={{position: 'relative', minHeight:'50vh', minWidth:600}}>
                <Box style={{
                    position:'absolute', left:5, top:0, right:5, bottom:3,
                    display:'flex', flexDirection: 'column'}}
                >
                    <ContentHeader>
                        <ControlBox>
                            {
                                tableData.length > 0
                                ?
                                <SearchBox value={searchText} onChangeText={setSearchText}/>
                                :
                                <span>파일을 선택해주세요.</span>
                            }
                            
                            <Box style={{marginLeft:10}}>
                            {
                                errorCount > 0 ?
                                    <VzmCheckbox
                                        dict={{id:'filterError', label:`오류항목(${errorCount}건)만 보기`}}
                                        value={filterError}
                                        onChange={onChangeFilterError}
                                    />
                                :
                                tableData.length > 0
                                ? <span>오류가 발견되지 않았습니다.</span>
                                : null
                            }
                            </Box>
                        </ControlBox>
                        <ToolBox>
                            {
                                loadingAny ?
                                <CircularProgress size={20} /> :
                                <CountLabel count={countToDisplay} />
                            }
                            <Box style={{
                                borderLeft:'1px solid #ccc',
                                height: '100%', marginLeft: 10
                            }}>&nbsp;</Box>
                            <input
                                type="file"
                                accept=".csv"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={onChooseFile}
                            />
                            {
                                fileInputRef.current && canAdd &&
                                <>
                                <Tooltip
                                    title="한글이 꺠지면 이 설정을 바꿔서 다시 파일찾기를 해보세요."
                                >
                                    <span style={{marginLeft:10}}>
                                        <VzmCheckbox
                                            dict={{
                                                id: 'useEucKr', label: '업로드 파일 완성형한글로 읽기',
                                            }}
                                            value={useEucKr}
                                            onChange={(id,value,err) => setUseEucKr(value)}
                                        />
                                    </span>
                                </Tooltip>
                                <Button
                                    variant={AppStyle.mui.contained}
                                    color={AppStyle.mui.primary}
                                    startIcon={<FindInPageIcon/>}
                                    onClick={onClickFindFile}
                                    style={{marginLeft:10}}
                                >
                                    파일 찾기
                                </Button>
                                </>
                            }
                        </ToolBox>
                    </ContentHeader>
                    <FlexyTable
                        name={tableProps.tableId}
                        tableProps={tableProps}
                        records={
                            filterError
                            ? tableData.filter((row)=>!row.ok)
                            : tableData
                        }
                        tools={tools}
                        search={searchText}
                        onClickTool={onClickToolOnRow}
                        onChangeCountToDisplay={setCountToDisplay}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                {
                    canAdd &&
                    <Button
                        color={AppStyle.mui.primary}
                        style={{marginRight:10}}
                        startIcon={<CloudUploadIcon />}
                        onClick={onClickUpload}
                        variant={AppStyle.mui.contained}
                        disabled={(errorCount > 0 && errorCount === tableData.length) || !changed || loadingAny}
                    >
                        데이터 업로드
                    </Button>
                }
                <Button
                    onClick={onClickClose}
                    color={AppStyle.mui.primary}
                    variant={AppStyle.mui.contained}
                    startIcon={<DoneIcon />}
                    disabled={loadingAny}
                >
                    닫기
                </Button>
            </DialogActions>
        </DraggableEditor>
    );
}